// TODO: Refactor
import Konva from 'konva';
import { Element } from './element';
import { Equipment } from '../equipment';
import { Point } from '../../../core/models/point';
import {
	appendBoxInnerMeshGrid,
	appendBoxMeshOpenFrame,
	appendSlicedBoxInnerMeshGrid,
	SLICED_BOX_FRONT_INNER_LINE_WIDTH_PX
} from './box-mesh-draw';
import { BarrierParams, BarrierSide, BarrierType, BARRIER_THICKNESS, BrandingParams, Extension, ExtensionType, brandingToCanvas, findBrandingImage, getBarrierMeshBarCenters, getBrandingFaces, getFreeSidesForBarrier, getMeshBarCenters, getTrapezeSideWallWedgeLayout, isBrandingSideEnabled, loadBrandingImage, mapBrandingToFaceSize, MESH_BAR_THICKNESS, RailsParams, RoundingParams, getBarrierMeshBarPlacements, shouldRenderBrandingOnEquipmentView } from './extension';
import { konvaSettings } from '../konvaSettings';
import {
	createInactiveEquipmentStripesImage,
	darkenColor,
	lightenColor,
	getElementFillColor,
	getTypeDefaultFillColor,
	getShapeStoredFillColor,
	INACTIVE_EQUIPMENT_STRIPE_PATTERN_SIZE,
	isStockFillColor,
	resolveRenderStrokeColor,
	resolveStoredFillColor,
	resolveStoredOuterFillColor
} from '../elementFillColor';
import { EquipmentElements } from './equipmentElements';
import { elementsGenerator } from './elementGenerator';
import { Figure } from '../figures/figure';
import { Rectangle } from '../figures/rectangle';
import { RectangleSliced } from '../figures/rectangle-sliced';
import { ShelfElement } from '../../../planogram/models/elements/shelfElement';
import { Utils } from '../../../core/services/utils.service';
import { UnitConverter } from '../../../core/utils/unitConverter';

export const BARRIER_CANVAS_NAME = 'barrier' as const;
export const BARRIER_ELEMENT_ID_ATTR = 'elementId' as const;
export const BARRIER_IDLE_COLOR_ATTR = 'idleColor' as const;

export type FrontCornerRadii = [number, number, number, number];

interface RoundedPathContext {
	beginPath(): void;
	moveTo(x: number, y: number): void;
	lineTo(x: number, y: number): void;
	arc(x: number, y: number, radius: number, startAngle: number, endAngle: number, counterclockwise?: boolean): void;
	closePath(): void;
	roundRect?(x: number, y: number, width: number, height: number, radii?: number | Array<number>): void;
}

export function getFrontCornerRadii(
	element: Element,
	width: number,
	height: number,
	flip = false
): FrontCornerRadii | null {
	if (!element.isExtensionActive(ExtensionType.Rounding) || width <= 0 || height <= 0) {
		return null;
	}

	const rounding: Extension<RoundingParams> = element.getExtension<RoundingParams>(ExtensionType.Rounding);
	const radius = Math.max(Math.min(width, height) / 2, 0);

	if (!radius) {
		return null;
	}

	const left = rounding.params.leftSideRadius ? radius : 0;
	const right = rounding.params.rightSideRadius ? radius : 0;

	if (!left && !right) {
		return null;
	}

	return flip ? [right, left, left, right] : [left, right, right, left];
}

/** True when the trapezoid side wedge expands toward the current 2D viewer (front or back). */
export function isTrapezeSideWallSlopeTowardViewer(
	flip: boolean,
	frontWidth: number,
	backWidth: number
): boolean {
	return flip ? frontWidth < backWidth : backWidth < frontWidth;
}

export function pathRoundedRect(
	ctx: RoundedPathContext,
	width: number,
	height: number,
	radii: FrontCornerRadii
): void {
	const [topLeft, topRight, bottomRight, bottomLeft] = radii.map((radius) =>
		Math.max(0, Math.min(radius, width / 2, height / 2))
	) as FrontCornerRadii;

	ctx.beginPath();

	if (typeof ctx.roundRect === 'function') {
		ctx.roundRect(0, 0, width, height, [topLeft, topRight, bottomRight, bottomLeft]);
		return;
	}

	ctx.moveTo(topLeft, 0);
	ctx.lineTo(width - topRight, 0);

	if (topRight > 0) {
		ctx.arc(width - topRight, topRight, topRight, -Math.PI / 2, 0);
	}

	ctx.lineTo(width, height - bottomRight);

	if (bottomRight > 0) {
		ctx.arc(width - bottomRight, height - bottomRight, bottomRight, 0, Math.PI / 2);
	}

	ctx.lineTo(bottomLeft, height);

	if (bottomLeft > 0) {
		ctx.arc(bottomLeft, height - bottomLeft, bottomLeft, Math.PI / 2, Math.PI);
	}

	ctx.lineTo(0, topLeft);

	if (topLeft > 0) {
		ctx.arc(topLeft, topLeft, topLeft, Math.PI, (Math.PI * 3) / 2);
	}

	ctx.closePath();
}

export function applyInactiveStripesRounding(
	stripesRect: Konva.Rect,
	element: Element,
	containerWidth: number,
	containerHeight: number,
	flip = false
): void {
	const stripeHeight = stripesRect.height();

	if (element.parent.is(EquipmentElements.HangBar)) {
		stripesRect.cornerRadius(Math.max(stripeHeight, 0) / 2);
		return;
	}

	const radii = getFrontCornerRadii(element, containerWidth, containerHeight, flip);

	if (!radii) {
		return;
	}

	const inset = 1;

	stripesRect.cornerRadius([
		Math.max(0, radii[0] - inset),
		Math.max(0, radii[1] - inset),
		Math.max(0, radii[2] - inset),
		Math.max(0, radii[3] - inset)
	]);
}

export function getRoundedClipConfig(
	width: number,
	height: number,
	radii: FrontCornerRadii | null
): { clip: { x: number; y: number; width: number; height: number } } | { clipFunc: (ctx: Konva.Context) => void } {
	if (!radii) {
		return { clip: { x: 0, y: 0, width, height } };
	}

	return { clipFunc: (ctx: Konva.Context) => pathRoundedRect(ctx, width, height, radii) };
}

export function getBarrierIdleColor(barrierType: BarrierType, barrierColor: string): string {
	return barrierType === BarrierType.Transparent ? konvaSettings.fillGreyColor : barrierColor;
}

export function drawBackWallRailsOnGroup(
	element: Element,
	equipment: Equipment | null | undefined,
	canvasElement: Konva.Group,
	scale: number,
	active: boolean
): void {
	const rails: Extension<RailsParams> = element.getExtension<RailsParams>(ExtensionType.Rails);

	if (!rails?.active) {
		return;
	}

	const sideWalls: Element | undefined = equipment?.allElements.find(
		(el: Element) => el.parent.is(EquipmentElements.SideWalls) && el.depend == element.depend
	);
	const sideWallsWidth: number = sideWalls
		? Utils.round(sideWalls.shapes[0].width + sideWalls.shapes[1].width)
		: 0;
	const railsWidth: number = Math.min(
		rails.params.railHolesWidth,
		Utils.round((element.Width - sideWallsWidth) / 2 - 0.02)
	);
	const connectedRails: boolean = rails.params.railHolesWidth >= Utils.round(element.Width / 2 - 0.02);
	const leftRailGroup: Konva.Group = new Konva.Group({
			name: 'left-rail',
			x: 0.5,
			y: 0.5,
			width: (railsWidth + 0.02) * scale,
			height: canvasElement.height() - 1
		}),
		leftRail: Konva.Rect = new Konva.Rect({
			name: 'fill-active fill-light',
			x: 0,
			y: 0,
			width: leftRailGroup.width(),
			height: leftRailGroup.height(),
			fill: active ? konvaSettings.activeColor : konvaSettings.fillGreyColor,
			strokeWidth: 0
		}),
		rightRailGroup: Konva.Group = new Konva.Group({
			name: 'right-rail',
			x: canvasElement.width() - (railsWidth + 0.02) * scale - 0.5,
			y: 0.5,
			width: (railsWidth + 0.02) * scale,
			height: canvasElement.height() - 1
		}),
		rightRail: Konva.Rect = new Konva.Rect({
			name: 'fill-active fill-light',
			x: 0,
			y: 0,
			width: rightRailGroup.width(),
			height: rightRailGroup.height(),
			fill: active ? konvaSettings.activeColor : konvaSettings.fillGreyColor,
			strokeWidth: 0
		}),
		height: number = element.shapes[0].begin.y + element.Height;

	if (sideWalls) {
		leftRailGroup.offsetX(-sideWalls.shapes[0].width * scale + 1);
		rightRailGroup.offsetX(sideWalls.shapes[1].width * scale - 1);
	}

	leftRailGroup.add(leftRail);
	rightRailGroup.add(rightRail);

	elementsGenerator[EquipmentElements.BackWall].getAnchorPoints(element).forEach((point: number) => {
		leftRailGroup.add(new Konva.Rect({
			x: 0.01 * scale,
			y: (height - point - rails.params.railHolesHeight) * scale,
			width: (railsWidth + (connectedRails ? 0.01 : 0)) * scale,
			height: rails.params.railHolesHeight * scale,
			fill: konvaSettings.fillWhiteColor,
			strokeWidth: 0
		}));
		rightRailGroup.add(new Konva.Rect({
			x: (connectedRails ? 0 : 0.01) * scale,
			y: (height - point - rails.params.railHolesHeight) * scale,
			width: (railsWidth + (connectedRails ? 0.01 : 0)) * scale,
			height: rails.params.railHolesHeight * scale,
			fill: konvaSettings.fillWhiteColor,
			strokeWidth: 0
		}));
	});

	canvasElement.add(leftRailGroup, rightRailGroup);
}

export function setBarrierNodesActive(root: Konva.Node, element: Element, active: boolean): void {
	const stage = root.getStage();
	const searchRoot = stage ?? (root instanceof Konva.Container ? root : null);

	if (!searchRoot) {
		return;
	}

	searchRoot.find(`.${BARRIER_CANVAS_NAME}`).forEach((node: Konva.Node) => {
		if (!(node instanceof Konva.Container) || node.getAttr(BARRIER_ELEMENT_ID_ATTR) !== element.id) {
			return;
		}

		const color = active ? konvaSettings.activeColor : node.getAttr(BARRIER_IDLE_COLOR_ATTR);

		if (!color) {
			return;
		}

		node.getChildren().forEach((child) => {
			if (!(child instanceof Konva.Shape)) {
				return;
			}

			if (child.stroke()) {
				child.stroke(color);
			}

			if (child.fill()) {
				child.fill(color);
			}
		});
	});
}

const NOT_AVAILABLE_LABEL: string = 'N';
const getShelfTooltipValues = (element: ShelfElement, equipment: Equipment): Partial<ITooltipLabels> => {
	const partitionAndSidesWallOnShelves = element.getElementsOnShelf
			? element.getElementsOnShelf.filter(el => ['Partition', 'SidesWall'].includes(el.parent.type))
			: [],
		partitionsShapes = partitionAndSidesWallOnShelves.length
			? Utils.to1DArray(partitionAndSidesWallOnShelves.map(el => el.shapes))
				.filter(el => el.isPointOn(null, element.getBoundingY().min) || el.isPointOn(null, element.getBoundingY().max))
			: [],
		widthPartitionAndSideWall = partitionsShapes.length
			? partitionsShapes.map(el => el.width).reduce((a, b) => Number(a) + Number(b))
			: 0,
		layoutMeters: number = element.layoutMeters,
		freeSpaceInShelf = element.linearMeters - layoutMeters - widthPartitionAndSideWall,
		productsGroupLength = element.products.map(p => p.length),
		quantitySKU = productsGroupLength.length ? productsGroupLength.reduce((a, b) => Number(a) + Number(b)) : 0;
	return {
		SHELF: Utils.getElementIndex(element, equipment) || NOT_AVAILABLE_LABEL,
		LENGTH: UnitConverter.getConvertValue(element.Width, 'distance', 'inches', 3),
		LINEAR_METERS: UnitConverter.getConvertValue(layoutMeters, 'distance', 'feet', 3),
		FREE_SPACE_ON_THE_SHELF_M: UnitConverter.getConvertValue(freeSpaceInShelf, 'distance', 'inches', 3),
		QUANTITY_OF_LAID_OUT_SKU: quantitySKU
	};
};
const getBoxTooltipValues = (element: Element, equipment: Equipment): Partial<ITooltipLabels> => {
	const layoutMeters: number = element.layoutMeters,
		freeSpaceInBox = element.Width - layoutMeters - element.shapes[0].wallWidth * 2,
		productsGroupLength = element.products.map((p) => p.length),
		quantitySKU = productsGroupLength.length ? productsGroupLength.reduce((a, b) => Number(a) + Number(b)) : 0;

	return {
		'BOX': Utils.getElementIndex(element, equipment) || NOT_AVAILABLE_LABEL,
		'LENGTH': UnitConverter.getConvertValue(element.Width, 'distance', 'inches', 3),
		'LINEAR_METERS': UnitConverter.getConvertValue(layoutMeters, 'distance', 'feet', 3),
		'FREE_SPACE_IN_THE_BOX_M': UnitConverter.getConvertValue(freeSpaceInBox, 'distance', 'inches', 3),
		'QUANTITY_OF_LAID_OUT_SKU': quantitySKU
	};
};

export interface IEquipmentSideState {
	getPoint(element: Element, equipment: Equipment): Point;
}

export interface IElementDrawer {
	setBeginPoint(point: Point): IElementDrawer;
	setEquipment(equipment: Equipment): IElementDrawer;
	setOffset(offset: number): IElementDrawer;
	setScale(scale: number): IElementDrawer;
	setShowLabels(show: boolean): IElementDrawer;
	setState(flip: boolean): IElementDrawer;
	draw(active: boolean): Konva.Group;
	drawTooltip(labels: ITooltipLabels, canvasElement: Konva.Group): Konva.Label;
}

export interface ITooltipLabels {
	SHELF: string | number;
	HANG_BAR: string | number;
	BOX: string | number;
	ONE_HOOK: string | number;
	LENGTH: string | number;
	DEPTH: string | number;
	LINEAR_METERS: string | number;
	FREE_SPACE_ON_THE_SHELF_M: string | number;
	FREE_SPACE_ON_THE_HANG_BAR_M: string | number;
	FREE_SPACE_IN_THE_BOX_M: string | number;
	FREE_SPACE_ON_THE_HOOK_M: string | number;
	QUANTITY_OF_LAID_OUT_SKU: string | number;
}

class ElementFrontState implements IEquipmentSideState {

	getPoint(element: Element, equipment: Equipment): Point {
		return {
			x: element.shapes[0].begin.x,
			y: equipment.Height - element.shapes[0].begin.y - element.shapes[0].height
		}
	}
}

class ElementBackState implements IEquipmentSideState {

	getPoint(element: Element, equipment: Equipment): Point {
		return {
			x: equipment.Width - element.shapes[0].begin.x - element.Width,
			y: equipment.Height - element.shapes[0].begin.y - element.shapes[0].height
		}
	}
}

class ElementDrawer implements IElementDrawer {

	protected _beginPoint: Point = { x: 0, y: 0 };
	protected _element: Element = null;
	protected _equipment: Equipment = null;
	protected _flip: boolean = false;
	protected _offset: number = 0;
	protected _scale: number = 0;
	protected _showLabels: boolean = true;
	protected _sideState: IEquipmentSideState;
	protected resolveFill(legacyWhenStock: string, shape?: Figure): string {
		const stored = getShapeStoredFillColor(shape ?? this._element.shapes?.[0]);

		return resolveStoredFillColor(stored, legacyWhenStock);
	}

	protected resolveOuterFill(legacyWhenStock: string, shape?: Figure): string {
		const stored = (shape ?? this._element.shapes?.[0])?.outerFillColor;

		return resolveStoredOuterFillColor(stored, legacyWhenStock);
	}

	protected resolveStroke(legacyWhenStock: string = getTypeDefaultFillColor(this._element.parent.type)): string {
		return resolveRenderStrokeColor(this._element, legacyWhenStock, false);
	}

	protected resolveInactiveStripeColor(): string {
		const stored = getShapeStoredFillColor(this._element.shapes?.[0]);

		if (!stored || isStockFillColor(stored)) {
			return getTypeDefaultFillColor(this._element.parent.type);
		}

		return getElementFillColor(this._element);
	}

	constructor(element: Element) {
		this._element = element;
	}

	public setBeginPoint(point: Point): IElementDrawer {
		this._beginPoint = point;

		return this;
	}

	public setEquipment(equipment: Equipment): IElementDrawer {
		this._equipment = equipment;

		return this;
	}

	public setOffset(offset: number): IElementDrawer {
		this._offset = offset;

		return this;
	}

	public setScale(scale: number): IElementDrawer {
		this._scale = scale;

		return this;
	}

	public setShowLabels(show: boolean): IElementDrawer {
		this._showLabels = show;

		return this;
	}

	public setState(flip: boolean = false): IElementDrawer {
		this._sideState = flip ? new ElementBackState() : new ElementFrontState();
		this._flip = flip;

		return this;
	}

	protected convertPointToCanvas(p: Point): Point {
		return {
			x: this._beginPoint.x + this._offset + p.x * this._scale,
			y: this._beginPoint.y + this._offset + p.y * this._scale
		};
	}

	protected applyFrontRounding(elementRect: Konva.Rect, elementGroup: Konva.Group): void {
		const radii = getFrontCornerRadii(this._element, elementGroup.width(), elementGroup.height(), this._flip);

		if (radii) {
			elementRect.cornerRadius(radii);
		}
	}

	public draw(active: boolean): Konva.Group {
		return new Konva.Group();
	};

	public drawTooltip(labels: ITooltipLabels, canvasElement: Konva.Group): Konva.Label {
		const values: Partial<ITooltipLabels> = this.getTooltipValues();

		if (!Object.keys(values).length) return null;

		const tooltip: Konva.Label = new Konva.Label({
			id: this._element.id,
			x: canvasElement.x() + canvasElement.width() / 2,
			y: canvasElement.y(),
			visible: false,
			opacity: 0.9
		});
		const tag: Konva.Tag = new Konva.Tag({
			fill: 'white',
			pointerDirection: 'down',
			pointerWidth: 10,
			pointerHeight: 10,
			lineJoin: 'round',
			shadowColor: 'black',
			shadowBlur: 10,
			shadowOpacity: 0.5
		});
		tooltip.add(tag);

		let tooltipText: string = '';
		Object.keys(values).forEach((key: string, idx: number) => {
			if (!idx) {
				tooltipText += `${labels[key]} ${values[key]}`;
			} else {
				tooltipText += `\n${labels[key]}: ${values[key]}`;
			}
		});

		const text = new Konva.Text({ fontSize: 12, text: tooltipText, padding: 5, fill: 'black' });
		tooltip.add(text);

		if (tooltip.y() - tooltip.height() - 10 < this._beginPoint.y + this._offset) {
			tooltip.y(canvasElement.y() + canvasElement.height());
			tag.pointerDirection('up');
		}

		if (tooltip.x() - tooltip.width() / 2 < 0) {
			tooltip.x(canvasElement.x() + canvasElement.width());
			tooltip.y(canvasElement.y() + canvasElement.height() / 2);
			tag.pointerDirection('left');
		}

		const totalWidth: number = Math.round(this._equipment.Width * this._scale + this._offset * 2 + this._beginPoint.x * 2);
		if (tooltip.x() + tooltip.width() / 2 > totalWidth) {
			tooltip.x(canvasElement.x());
			tooltip.y(canvasElement.y() + canvasElement.height() / 2);
			tag.pointerDirection('right');
		}

		return tooltip;
	}

	protected createLabel(text: string, fill: string = konvaSettings.fillWhiteColor): Konva.Group {
		if (!this._showLabels) {
			return new Konva.Group({ name: 'label', visible: false, width: 0, height: 0 });
		}

		const labelGroup: Konva.Group = new Konva.Group({
				name: 'label'
			}),
			labelText: Konva.Text = new Konva.Text({
				text: text,
				fontSize: 12,
				x: 3,
				y: 2
			}),
			labelRect: Konva.Rect = new Konva.Rect({
				width: labelText.getWidth() + 6,
				height: labelText.getHeight() + 4,
				stroke: 'black',
				strokeWidth: 1,
				fill: fill
			});

		labelGroup.add(labelRect);
		labelGroup.add(labelText);
		labelGroup.setAttrs({ width: labelRect.width(), height: labelRect.height() });

		return labelGroup;
	}

	protected drawBarrier(elementGroup: Konva.Group, active: boolean, offsets?: { x?: number, y?: number }) {
		const extension = this._element.getExtension<BarrierParams>(ExtensionType.Barrier);

		if (!extension || !extension.active) {
			return;
		}

		const { barrierType, barrierHeight, barrierColor } = extension.params;

		if (barrierType === BarrierType.None) {
			return;
		}

		const equipment = this._equipment;

		if (!equipment) {
			return;
		}

		const facing = this._flip ? BarrierSide.Back : BarrierSide.Front;
		const sides = getFreeSidesForBarrier(this._element, equipment);

		if (!sides.includes(facing)) {
			return;
		}

		const shape = this._element.shapes[0];
		const length =
			Math.max(shape.frontWidth ?? 0, shape.backWidth ?? 0, this._element.Width) + 2 * BARRIER_THICKNESS;

		const pixelThickness = BARRIER_THICKNESS * this._scale;
		const pixelHeight = barrierHeight * this._scale;
		const pixelWidth = length * this._scale;
		const pixelOffsetX = offsets?.x ? offsets.x * this._scale : 0;
		const pixelOffsetY = offsets?.y ? offsets.y * this._scale : 0;
		const idleColor = getBarrierIdleColor(barrierType, barrierColor);
		const color = active ? konvaSettings.activeColor : idleColor;

		const barrierGroup = new Konva.Group({
			name: BARRIER_CANVAS_NAME,
			x: pixelOffsetX - pixelThickness,
			y: pixelOffsetY - pixelHeight,
			[BARRIER_ELEMENT_ID_ATTR]: this._element.id,
			[BARRIER_IDLE_COLOR_ATTR]: idleColor
		});
	
		if (barrierType !== BarrierType.Mesh) {
			barrierGroup.add(
				new Konva.Rect({
					width: pixelWidth,
					height: pixelHeight,
					fill: barrierType === BarrierType.Solid ? color : undefined,
					stroke: color,
					strokeWidth: 1,
					dash: barrierType === BarrierType.Transparent ? [3, 3] : undefined
				})
			);
		} else {
			barrierGroup.add(
				new Konva.Rect({
					width: pixelWidth,
					height: pixelThickness,
					fill: color
				}),
				new Konva.Rect({
					y: pixelHeight - pixelThickness,
					width: pixelWidth,
					height: pixelThickness,
					fill: color
				}),
				...getBarrierMeshBarPlacements(length).map((placement, index, { length }) => {
					const pixelOffsetFix = index === length - 1 ? 1 : 0;

					return new Konva.Rect({
						x: placement * this._scale - pixelOffsetFix,
						width: pixelThickness + pixelOffsetFix,
						height: pixelHeight,
						fill: color
					});
				})
			);
		}

		elementGroup.add(barrierGroup);
	}

	protected drawBranding(target: Konva.Group, shapeIndex = 0) {
		const branding = this._element.getExtension<BrandingParams>(ExtensionType.Branding);

		if (!branding?.active) {
			return;
		}

		if (!shouldRenderBrandingOnEquipmentView(this._element, this._equipment, this._flip)) {
			return;
		}

		const placed = findBrandingImage(branding.params.images, shapeIndex);

		if (!placed) {
			return;
		}

		const face = getBrandingFaces(this._element).find((item) => item.shapeIndex === shapeIndex);
		const width = target.width();
		const height = target.height();

		if (!face || !width || !height) {
			return;
		}

		const clipGroup = new Konva.Group({
			...getRoundedClipConfig(
				width,
				height,
				getFrontCornerRadii(this._element, width, height, this._flip)
			),
			listening: false
		});

		target.add(clipGroup);

		loadBrandingImage(placed.image)
			.then((source) => {
				if (!clipGroup.getLayer()) {
					return;
				}

				clipGroup.add(
					new Konva.Image({
						image: source,
						...brandingToCanvas(placed, this._scale, height),
						rotation: branding.params.rotation,
						opacity: branding.params.opacity,
						listening: false
					})
				);
				clipGroup.getLayer()?.batchDraw();
			})
			.catch(() => undefined);
	}

	protected drawSideWallWedgeBranding(
		wallGroup: Konva.Group,
		wallShape: Figure,
		wedgeWidthM: number,
		slopeTowardViewer: boolean,
		isLeftWall: boolean,
		innerWalls: boolean
	) {
		const branding = this._element.getExtension<BrandingParams>(ExtensionType.Branding);
		const faceDepthM = wallShape.depth ?? 0;
		const faceHeightM = wallShape.height ?? 0;

		if (!branding?.active || wedgeWidthM <= 0 || !faceDepthM || !faceHeightM) {
			return;
		}

		const layout = getTrapezeSideWallWedgeLayout(
			slopeTowardViewer,
			isLeftWall,
			innerWalls,
			(wallShape.width ?? 0) * this._scale,
			wedgeWidthM,
			faceDepthM
		);

		if (!isBrandingSideEnabled(branding.params, layout.side)) {
			return;
		}

		const placed = findBrandingImage(branding.params.images, layout.shapeIndex);
		const face = placed
			? getBrandingFaces(this._element).find((item) => item.shapeIndex === layout.shapeIndex)
			: undefined;

		if (!placed || !face) {
			return;
		}

		const faceHeightPx = faceHeightM * this._scale;
		const faceWidthPx = faceDepthM * this._scale;
		const wedgeWidthPx = wedgeWidthM * this._scale;
		const clipWidth = Math.max(0, wedgeWidthPx - layout.widthReduce);

		if (clipWidth <= 0) {
			return;
		}

		const mapped = mapBrandingToFaceSize(placed, face, faceDepthM, faceHeightM);
		const clipGroup = new Konva.Group({
			x: layout.groupX,
			y: 0,
			width: wedgeWidthPx,
			height: faceHeightPx,
			clip: { x: layout.clipX, y: 0, width: clipWidth, height: faceHeightPx },
			listening: false
		});

		wallGroup.add(clipGroup);

		const faceGroup = new Konva.Group({
			width: faceWidthPx,
			height: faceHeightPx,
			scaleX: layout.compression,
			listening: false,
			...(layout.scaleFromOuter ? { offsetX: faceWidthPx, x: wedgeWidthPx } : {})
		});

		clipGroup.add(faceGroup);

		loadBrandingImage(mapped.image)
			.then((source) => {
				if (!faceGroup.getLayer()) {
					return;
				}

				faceGroup.add(
					new Konva.Image({
						image: source,
						...brandingToCanvas(mapped, this._scale, faceHeightPx),
						rotation: branding.params.rotation,
						opacity: branding.params.opacity,
						listening: false
					})
				);
				faceGroup.getLayer()?.batchDraw();
			})
			.catch(() => undefined);
	}

	protected drawHiddenLines(
		canvasElement: Konva.Group,
		stripeColor?: string,
		shapeRect?: Konva.Rect
	): Konva.Rect {
		const patternStripeColor = stripeColor ?? this.resolveInactiveStripeColor();
		const stripesRect: Konva.Rect = new Konva.Rect({
			name: 'stripes',
			x: 1,
			y: 1,
			width: Math.max(canvasElement.width() - 2, 0),
			height: Math.max(canvasElement.height() - 2, 0),
			fillPatternImage: createInactiveEquipmentStripesImage(patternStripeColor) as unknown as HTMLImageElement,
			fillPatternRepeat: 'repeat',
			fillPatternScaleX: 20 / INACTIVE_EQUIPMENT_STRIPE_PATTERN_SIZE,
			fillPatternScaleY: 20 / INACTIVE_EQUIPMENT_STRIPE_PATTERN_SIZE,
			listening: false
		});
		const shapeCornerRadius = shapeRect?.cornerRadius();
		const stripeHeight = stripesRect.height();

		if (shapeCornerRadius !== undefined && shapeCornerRadius !== 0) {
			if (Array.isArray(shapeCornerRadius)) {
				stripesRect.cornerRadius(
					shapeCornerRadius.map((radius) =>
						Math.min(Math.max(0, (typeof radius === 'number' ? radius : 0) - 1), stripeHeight / 2)
					) as FrontCornerRadii
				);
			} else if (typeof shapeCornerRadius === 'number') {
				stripesRect.cornerRadius(Math.min(Math.max(0, shapeCornerRadius - 1), stripeHeight / 2));
			}
		} else {
			applyInactiveStripesRounding(
				stripesRect,
				this._element,
				canvasElement.width(),
				canvasElement.height(),
				this._flip
			);
		}

		canvasElement.add(stripesRect);

		return stripesRect;
	}

	protected drawBackWallRails(active: boolean, canvasElement: Konva.Group): void {
		drawBackWallRailsOnGroup(this._element, this._equipment, canvasElement, this._scale, active);
	}

	protected getTooltipValues(): Partial<ITooltipLabels> {
		return {};
	}
}

export class RectangleBaseElementDrawer extends ElementDrawer {

	public draw(active: boolean): Konva.Group {
		const point: Point = this.convertPointToCanvas(this._sideState.getPoint(this._element, this._equipment));
		const elementGroup: Konva.Group = new Konva.Group({
			name: this._element.parent.type,
			x: point.x + 0.5,
			y: point.y + 0.5,
			width: this._element.shapes[0].width * this._scale - 1,
			height: this._element.shapes[0].height * this._scale - 1
		});
		let elementRect: Konva.Rect, labelText: string, labelFill: string;

		if (!this._element.isExtensionActive(ExtensionType.Availability)) {
			elementRect = new Konva.Rect({
				name: 'disabled stroke-dark stroke-active fill-hover-light',
				x: 0,
				y: 0,
				width: elementGroup.width(),
				height: elementGroup.height(),
				stroke: active ? konvaSettings.activeColor : this.resolveStroke(),
				strokeWidth: 1,
				fill: konvaSettings.fillGreyColor
			});
			labelText = NOT_AVAILABLE_LABEL;
			labelFill = konvaSettings.fillGreyColor;
			elementGroup.add(elementRect);
		} else {
			elementRect = new Konva.Rect({
				name: 'dark active fill-hover-light',
				x: 0,
				y: 0,
				width: elementGroup.width(),
				height: elementGroup.height(),
				stroke: active ? konvaSettings.activeColor : this.resolveStroke(),
				strokeWidth: 1,
				fill: active ? konvaSettings.activeColor : this.resolveFill(konvaSettings.fillGreyColor)
			});
			labelText = String(1);
			labelFill = konvaSettings.fillWhiteColor;
			elementGroup.add(elementRect);
		}

		this.applyFrontRounding(elementRect, elementGroup);

		if (!this._element.isExtensionActive(ExtensionType.Availability)) {
			this.drawHiddenLines(elementGroup, undefined, elementRect);
		}

		this.drawBranding(elementGroup);
		this.drawBarrier(elementGroup, active);

		const labelGroup: Konva.Group = this.createLabel(labelText, labelFill);
		labelGroup.setAttrs({ x: -labelGroup.width(), y: 0 });
		elementGroup.add(labelGroup);

		return elementGroup;
	}

	protected getTooltipValues(): Partial<ITooltipLabels> {
		return getShelfTooltipValues(this._element as ShelfElement, this._equipment);
	}
}

export class TrapezeBaseElementDrawer extends ElementDrawer {

	public draw(active: boolean): Konva.Group {
		const point: Point = this.convertPointToCanvas(this._sideState.getPoint(this._element, this._equipment));
		const width: number = Math.max(this._element.shapes[0].frontWidth, this._element.shapes[0].backWidth);
		const backOffset: number = (width - this._element.shapes[0].backWidth) / 2 * this._scale;
		const frontOffset: number = (width - this._element.shapes[0].frontWidth) / 2 * this._scale;
		const visibleSideNames: string = 'dark active fill-hover-light';
		const invisibleSideNames: string = 'fill-light stroke-dark stroke-active fill-hover-dark';
		const disabledSideNames: string = 'disabled stroke-dark stroke-active fill-hover-light';
		const elementGroup: Konva.Group = new Konva.Group({
			name: this._element.parent.type,
			x: point.x + 0.5,
			y: point.y + 0.5,
			width: width * this._scale - 1,
			height: this._element.shapes[0].height * this._scale - 1
		});
		const backGroup: Konva.Group = new Konva.Group({
			x: backOffset + 0.5,
			y: 0.5,
			width: elementGroup.width() - backOffset * 2 - 1,
			height: elementGroup.height() - 1,
		});
		const backRect: Konva.Rect = new Konva.Rect({
			x: 0,
			y: 0,
			width: backGroup.width(),
			height: backGroup.height(),
			stroke: active ? konvaSettings.activeColor : this.resolveStroke(),
			strokeWidth: 1,
			fill: konvaSettings.fillGreyColor
		});
		backGroup.add(backRect);
		const frontGroup: Konva.Group = new Konva.Group({
			x: frontOffset + 0.5,
			y: 0.5,
			width: elementGroup.width() - frontOffset * 2 - 1,
			height: elementGroup.height() - 1,
		});
		const frontRect: Konva.Rect = new Konva.Rect({
			x: 0,
			y: 0,
			width: frontGroup.width(),
			height: frontGroup.height(),
			stroke: active ? konvaSettings.activeColor : this.resolveStroke(),
			strokeWidth: 1,
			fill: this.resolveFill(konvaSettings.fillGreyColor)
		});
		frontGroup.add(frontRect);

		let labelText: string, labelFill: string, frontNames: string, frontColor: string;

		if (!this._element.isExtensionActive(ExtensionType.Availability)) {
			labelText = NOT_AVAILABLE_LABEL;
			labelFill = konvaSettings.fillGreyColor;
			frontNames = disabledSideNames;
			frontColor = konvaSettings.fillGreyColor;
		} else {
			labelText = String(1);
			labelFill = konvaSettings.fillWhiteColor;
			frontNames = visibleSideNames;
			frontColor = this.resolveFill(konvaSettings.fillGreyColor);
		}

		const availabilityActive = this._element.isExtensionActive(ExtensionType.Availability);
		const slopeParams = {
			frontOffset,
			backOffset,
			frontWidth: this._element.shapes[0].frontWidth,
			backWidth: this._element.shapes[0].backWidth,
			active,
			availabilityActive,
			groupWidth: elementGroup.width(),
			groupHeight: elementGroup.height()
		};

		if (this._flip) {
			frontRect.name(invisibleSideNames);
			frontRect.fill(konvaSettings.fillGreyColor);
			backRect.name(frontNames);
			backRect.fill(active && availabilityActive ? konvaSettings.activeColor : frontColor);
			elementGroup.add(frontGroup);
			this.addTrapezeFrontSlopePanels(elementGroup, slopeParams);
			elementGroup.add(backGroup);
		} else {
			backRect.name(invisibleSideNames);
			backRect.fill(konvaSettings.fillGreyColor);
			frontRect.name(frontNames);
			frontRect.fill(active && availabilityActive ? konvaSettings.activeColor : frontColor);
			elementGroup.add(backGroup);
			this.addTrapezeFrontSlopePanels(elementGroup, slopeParams);
			elementGroup.add(frontGroup);
		}

		if (!this._element.isExtensionActive(ExtensionType.Availability)) {
			const visibleGroup = this._flip ? backGroup : frontGroup;
			const visibleRect = this._flip ? backRect : frontRect;

			this.applyFrontRounding(visibleRect, visibleGroup);
			this.drawHiddenLines(visibleGroup, undefined, visibleRect);
		}

		this.drawBranding(this._flip ? backGroup : frontGroup);
		this.drawBarrier(elementGroup, active);

		const labelGroup: Konva.Group = this.createLabel(labelText, labelFill);
		labelGroup.setAttrs({ x: -labelGroup.width(), y: 0 });
		elementGroup.add(labelGroup);

		return elementGroup;
	}

	private addTrapezeFrontSlopePanels(
		elementGroup: Konva.Group,
		params: {
			frontOffset: number;
			backOffset: number;
			frontWidth: number;
			backWidth: number;
			active: boolean;
			availabilityActive: boolean;
			groupWidth: number;
			groupHeight: number;
		}
	) {
		const {
			frontOffset,
			backOffset,
			frontWidth,
			backWidth,
			active,
			availabilityActive,
			groupWidth,
			groupHeight
		} = params;
		const hiddenWider = this._flip ? frontWidth > backWidth : backWidth > frontWidth;

		if (!hiddenWider) {
			return;
		}

		const visibleOffsetPx = this._flip ? backOffset : frontOffset;
		const insetPx = this._flip ? backOffset : frontOffset;
		const visibleWidthPx = groupWidth - insetPx * 2 - 1;
		const panelHeight = groupHeight - 1;
		const baseColor = active
			? konvaSettings.activeColor
			: this.resolveFill(konvaSettings.fillGreyColor);
		const fill = availabilityActive ? lightenColor(baseColor) : konvaSettings.fillWhiteColor;
		const slopeNames = availabilityActive
			? 'fill-slope active stroke-dark stroke-active fill-hover-light'
			: 'disabled stroke-dark stroke-active fill-hover-light';

		const addPanel = (x: number, width: number) => {
			if (width <= 0) {
				return;
			}

			const panelGroup = new Konva.Group({
				x: x + 1,
				y: 1,
				width: width - 1,
				height: panelHeight - 1
			});
			const panelRect = new Konva.Rect({
				name: slopeNames,
				x: 0,
				y: 0,
				width: width - 1,
				height: panelHeight - 1,
				fill,
				strokeWidth: 0,
				listening: false
			});

			panelGroup.add(panelRect);
			elementGroup.add(panelGroup);

			if (!availabilityActive) {
				this.drawHiddenLines(panelGroup, undefined, panelRect);
			}
		};

		addPanel(0, visibleOffsetPx);
		addPanel(visibleOffsetPx + visibleWidthPx, groupWidth - visibleOffsetPx - visibleWidthPx - 0.5);
	}

	protected drawBarrier(elementGroup: Konva.Group, active: boolean, offsets?: { x?: number, y?: number }) {
		const extension = this._element.getExtension<BarrierParams>(ExtensionType.Barrier);

		if (!extension || !extension.active) {
			return;
		}

		const { barrierType, barrierHeight, barrierColor } = extension.params;

		if (barrierType === BarrierType.None) {
			return;
		}

		const equipment = this._equipment;

		if (!equipment) {
			return;
		}

		const facing = this._flip ? BarrierSide.Back : BarrierSide.Front;
		const sides = getFreeSidesForBarrier(this._element, equipment);

		if (!sides.includes(facing)) {
			return;
		}

		const shape = this._element.shapes[0];
		const frontWidth = shape.frontWidth ?? 0;
		const backWidth = shape.backWidth ?? 0;
		const maxWidth = Math.max(frontWidth, backWidth);
		const frontOffset = (maxWidth - frontWidth) / 2;
		const backOffset = (maxWidth - backWidth) / 2;
		const taperWidth = Math.abs(backOffset - frontOffset);
		const pixelOffsetY = offsets?.y ? offsets.y * this._scale : 0;
		const panelParams = { barrierType, barrierHeight, barrierColor, active, pixelOffsetY };
		const showSidePanels =
			taperWidth > 0 &&
			(this._flip ? frontWidth > backWidth : backWidth > frontWidth);

		if (!this._flip) {
			if (sides.includes(BarrierSide.Front)) {
				this.addTrapezeBarrierPanel(elementGroup, frontOffset, frontWidth, panelParams);
			}
		} else if (sides.includes(BarrierSide.Back)) {
			this.addTrapezeBarrierPanel(elementGroup, backOffset, backWidth, panelParams);
		}

		if (showSidePanels) {
			if (sides.includes(BarrierSide.Left)) {
				this.addTrapezeBarrierPanel(elementGroup, Math.min(frontOffset, backOffset), taperWidth, panelParams);
			}

			if (sides.includes(BarrierSide.Right)) {
				const rightOffset = Math.max(frontOffset + frontWidth, backOffset + backWidth) - taperWidth;

				this.addTrapezeBarrierPanel(elementGroup, rightOffset, taperWidth, panelParams);
			}
		}
	}

	private addTrapezeBarrierPanel(
		elementGroup: Konva.Group,
		offsetXMeters: number,
		widthMeters: number,
		params: {
			barrierType: BarrierType;
			barrierHeight: number;
			barrierColor: string;
			active: boolean;
			pixelOffsetY: number;
		}
	) {
		const { barrierType, barrierHeight, barrierColor, active, pixelOffsetY } = params;
		const length = widthMeters + 2 * BARRIER_THICKNESS;
		const pixelThickness = BARRIER_THICKNESS * this._scale;
		const pixelHeight = barrierHeight * this._scale;
		const pixelWidth = length * this._scale;
		const idleColor = getBarrierIdleColor(barrierType, barrierColor);
		const color = active ? konvaSettings.activeColor : idleColor;

		const barrierGroup = new Konva.Group({
			name: BARRIER_CANVAS_NAME,
			x: offsetXMeters * this._scale - pixelThickness,
			y: pixelOffsetY - pixelHeight,
			[BARRIER_ELEMENT_ID_ATTR]: this._element.id,
			[BARRIER_IDLE_COLOR_ATTR]: idleColor
		});

		if (barrierType !== BarrierType.Mesh) {
			barrierGroup.add(
				new Konva.Rect({
					width: pixelWidth,
					height: pixelHeight,
					fill: barrierType === BarrierType.Solid ? color : undefined,
					stroke: color,
					strokeWidth: 1,
					dash: barrierType === BarrierType.Transparent ? [3, 3] : undefined
				})
			);
		} else {
			barrierGroup.add(
				new Konva.Rect({
					width: pixelWidth,
					height: pixelThickness,
					fill: color
				}),
				new Konva.Rect({
					y: pixelHeight - pixelThickness,
					width: pixelWidth,
					height: pixelThickness,
					fill: color
				}),
				...getBarrierMeshBarPlacements(length).map((placement, index, { length: count }) => {
					const pixelOffsetFix = index === count - 1 ? 1 : 0;

					return new Konva.Rect({
						x: placement * this._scale - pixelOffsetFix,
						width: pixelThickness + pixelOffsetFix,
						height: pixelHeight,
						fill: color
					});
				})
			);
		}

		elementGroup.add(barrierGroup);
	}

	protected getTooltipValues() {
		return getShelfTooltipValues(this._element as ShelfElement, this._equipment);
	}
}

export class BackWallElementDrawer extends ElementDrawer {

	public draw(active: boolean): Konva.Group {
		const point: Point = this.convertPointToCanvas(this._sideState.getPoint(this._element, this._equipment));
		const elementGroup: Konva.Group = new Konva.Group({
			name: this._element.parent.type,
			x: point.x + 0.5,
			y: point.y + 0.5,
			width: this._element.shapes[0].width * this._scale - 1,
			height: this._element.shapes[0].height * this._scale - 1
		});

		elementGroup.add(new Konva.Rect({
			name: 'stroke-dark stroke-active',
			x: 0,
			y: 0,
			width: elementGroup.width(),
			height: elementGroup.height(),
			stroke: active ? konvaSettings.activeColor : this.resolveStroke(),
			strokeWidth: 1,
			fill: this.resolveFill(konvaSettings.fillWhiteColor)
		}));

		this.drawBranding(elementGroup);

		if (this._element.isExtensionActive(ExtensionType.Rails) && !this._flip) {
			this.drawBackWallRails(active, elementGroup);
		}

		return elementGroup;
	}
}

export class CentralPillarElementDrawer extends ElementDrawer {

	public draw(active: boolean): Konva.Group {
		const point: Point = this.convertPointToCanvas(this._sideState.getPoint(this._element, this._equipment));
		const elementGroup: Konva.Group = new Konva.Group({
			name: this._element.parent.type,
			x: point.x + 0.5,
			y: point.y + 0.5,
			width: this._element.shapes[0].width * this._scale - 1,
			height: this._element.shapes[0].height * this._scale - 1
		});

		elementGroup.add(new Konva.Rect({
			name: 'stroke-dark stroke-active',
			x: 0,
			y: 0,
			width: elementGroup.width(),
			height: elementGroup.height(),
			stroke: active ? konvaSettings.activeColor : this.resolveStroke(),
			strokeWidth: 1,
			fill: this.resolveFill(konvaSettings.fillWhiteColor)
		}));

		this.drawBranding(elementGroup);

		if (this._element.isExtensionActive(ExtensionType.Rails)) {
			this.drawBackWallRails(active, elementGroup);
		}

		return elementGroup;
	}
}

export class HookWallElementDrawer extends ElementDrawer {

	public draw(active: boolean): Konva.Group {
		const point: Point = this.convertPointToCanvas(this._sideState.getPoint(this._element, this._equipment));
		const elementGroup: Konva.Group = new Konva.Group({
			name: this._element.parent.type,
			x: point.x + 0.5,
			y: point.y + 0.5,
			width: this._element.shapes[0].width * this._scale - 1,
			height: this._element.shapes[0].height * this._scale - 1
		});

		elementGroup.add(new Konva.Rect({
			name: 'stroke-dark stroke-active',
			x: 0,
			y: 0,
			width: elementGroup.width(),
			height: elementGroup.height(),
			stroke: active ? konvaSettings.activeColor : this.resolveStroke(),
			strokeWidth: 1,
			fill: this.resolveFill(konvaSettings.fillWhiteColor)
		}));

		return elementGroup;
	}
}

export class PerforatedWallElementDrawer extends ElementDrawer {

	public draw(active: boolean): Konva.Group {
		const point: Point = this.convertPointToCanvas(this._sideState.getPoint(this._element, this._equipment));
		const elementGroup: Konva.Group = new Konva.Group({
			name: this._element.parent.type,
			x: point.x + 0.5,
			y: point.y + 0.5,
			width: this._element.shapes[0].width * this._scale - 1,
			height: this._element.shapes[0].height * this._scale - 1
		});

		elementGroup.add(new Konva.Rect({
			name: 'stroke-dark stroke-active',
			x: 0,
			y: 0,
			width: elementGroup.width(),
			height: elementGroup.height(),
			stroke: active ? konvaSettings.activeColor : this.resolveStroke(),
			strokeWidth: 1,
			fill: this.resolveFill(konvaSettings.fillWhiteColor)
		}));

		this._equipment.allElements.forEach((el: Element) => {
			if (el.depend == this._element) {
				elementGroup.add(new Konva.Rect({
					name: 'stroke-light stroke-active',
					x: (el.shapes[0].begin.x - el.depend.shapes[0].begin.x) * this._scale,
					y: (el.depend.shapes[0].height - el.shapes[0].begin.y + el.depend.shapes[0].begin.y - el.shapes[0].height) * this._scale,
					width: 0.01 * this._scale,
					height: 0.01 * this._scale,
					stroke: active ? konvaSettings.activeColor : konvaSettings.fillGreyColor,
					strokeWidth: 1,
					fill: konvaSettings.fillWhiteColor
				}));
			}
		});

		return elementGroup;
	}
}

class SideWallsFrontState implements IEquipmentSideState {

	getPoint(element: Element, equipment: Equipment): Point {
		const y: number = Math.max(element.shapes[0].begin.y, element.shapes[1].begin.y);
		const height: number = Math.max(element.shapes[0].height, element.shapes[1].height);

		return {
			x: element.depend.getBoundingX().min,
			y: equipment.Height - y - height
		}
	}
}

class SideWallsBackState implements IEquipmentSideState {

	getPoint(element: Element, equipment: Equipment): Point {
		const y: number = Math.max(element.shapes[0].begin.y, element.shapes[1].begin.y);
		const height: number = Math.max(element.shapes[0].height, element.shapes[1].height);

		return {
			x: equipment.Width - element.depend.getBoundingX().max,
			y: equipment.Height - y - height
		}
	}
}

export class SideWallsElementDrawer extends ElementDrawer {

	public setState(flip: boolean = false): IElementDrawer {
		this._sideState = flip ? new SideWallsBackState() : new SideWallsFrontState();
		this._flip = flip;

		return this;
	}

	private getSideWallTrapezeWedgeStyle(
		shape: Figure,
		slopeTowardViewer: boolean
	): { fill: string; names: string } {
		if (slopeTowardViewer) {
			return {
				fill: this.resolveFill(konvaSettings.fillGreyColor, shape),
				names: 'stroke-dark stroke-active fill-dark'
			};
		}

		return {
			fill: this.resolveOuterFill(konvaSettings.fillGreyColor, shape),
			names: 'stroke-dark stroke-active fill-slope'
		};
	}

	public draw(active: boolean): Konva.Group {
		const point: Point = this.convertPointToCanvas(this._sideState.getPoint(this._element, this._equipment));
		const elementGroup: Konva.Group = new Konva.Group({
			name: this._element.parent.type,
			x: point.x,
			y: point.y,
			width: this._element.depend.Width * this._scale,
			height: this._element.Height * this._scale
		});

		const dependIsTrapeze = this._element.depend.parent.is(EquipmentElements.BaseTrapeze);
		const baseShape: Figure = this._element.depend.shapes[0];
		const frontWidth = baseShape.frontWidth ?? 0;
		const backWidth = baseShape.backWidth ?? 0;
		const slopeTowardViewer = dependIsTrapeze
			? isTrapezeSideWallSlopeTowardViewer(this._flip, frontWidth, backWidth)
			: false;
		const wedgeWidthM = dependIsTrapeze ? Math.abs(frontWidth - backWidth) / 2 : 0;
		let startLeftWall: number = 0,
			endLeftWall: number = this._element.shapes[0].width,
			// true: кромка на внутрішніх ребрах; false: на зовнішніх
			innerWalls: boolean = frontWidth < backWidth;

		if (dependIsTrapeze) {
			innerWalls = !slopeTowardViewer;
			endLeftWall = Math.abs(frontWidth - backWidth) / 2 + this._element.shapes[0].width;
		}

		const leftShape = this._element.shapes[0];
		const leftWedgeStyle = dependIsTrapeze
			? this.getSideWallTrapezeWedgeStyle(leftShape, slopeTowardViewer)
			: { fill: konvaSettings.fillGreyColor, names: 'stroke-dark stroke-active' };

		const leftWallGroup: Konva.Group = new Konva.Group({
			x: startLeftWall * this._scale,
			y: (this._element.Height - this._element.shapes[0].height) * this._scale,
			width: (endLeftWall - startLeftWall) * this._scale,
			height: this._element.shapes[0].height * this._scale
		});

		leftWallGroup.add(new Konva.Rect({
			name: leftWedgeStyle.names,
			x: 0,
			y: 0,
			width: leftWallGroup.width(),
			height: leftWallGroup.height(),
			stroke: active ? konvaSettings.activeColor : this.resolveStroke(),
			strokeWidth: 1,
			fill: leftWedgeStyle.fill
		}));

		const leftPanelFill = this.resolveFill(konvaSettings.fillGreyColor, leftShape);

		leftWallGroup.add(new Konva.Rect({
			name: 'stroke-dark stroke-active fill-dark',
			x: innerWalls ? leftWallGroup.width() - leftShape.width * this._scale : 0,
			y: 0,
			width: leftShape.width * this._scale,
			height: leftWallGroup.height(),
			stroke: active ? konvaSettings.activeColor : this.resolveStroke(),
			strokeWidth: 1,
			fill: leftPanelFill
		}));

		this.drawSideWallWedgeBranding(leftWallGroup, leftShape, wedgeWidthM, slopeTowardViewer, true, innerWalls);

		elementGroup.add(leftWallGroup);

		let startRightWall: number = this._element.depend.Width - this._element.shapes[1].width,
			endRightWall: number = this._element.depend.Width;

		if (dependIsTrapeze) {
			startRightWall = this._element.depend.Width - Math.abs(frontWidth - backWidth) / 2 - this._element.shapes[1].width;
		}

		const rightShape = this._element.shapes[1];
		const rightWedgeStyle = dependIsTrapeze
			? this.getSideWallTrapezeWedgeStyle(rightShape, slopeTowardViewer)
			: { fill: konvaSettings.fillGreyColor, names: 'stroke-dark stroke-active' };

		const rightWallGroup: Konva.Group = new Konva.Group({
			x: startRightWall * this._scale,
			y: (this._element.Height - this._element.shapes[1].height) * this._scale,
			width: (endRightWall - startRightWall) * this._scale,
			height: this._element.shapes[1].height * this._scale
		});

		rightWallGroup.add(new Konva.Rect({
			name: rightWedgeStyle.names,
			x: 0,
			y: 0,
			width: rightWallGroup.width(),
			height: rightWallGroup.height(),
			stroke: active ? konvaSettings.activeColor : this.resolveStroke(),
			strokeWidth: 1,
			fill: rightWedgeStyle.fill
		}));

		const rightPanelFill = this.resolveFill(konvaSettings.fillGreyColor, rightShape);

		rightWallGroup.add(new Konva.Rect({
			name: 'stroke-dark stroke-active fill-dark',
			x: innerWalls ? 0 : rightWallGroup.width() - rightShape.width * this._scale,
			y: 0,
			width: rightShape.width * this._scale,
			height: rightWallGroup.height(),
			stroke: active ? konvaSettings.activeColor : this.resolveStroke(),
			strokeWidth: 1,
			fill: rightPanelFill
		}));

		this.drawSideWallWedgeBranding(rightWallGroup, rightShape, wedgeWidthM, slopeTowardViewer, false, innerWalls);

		elementGroup.add(rightWallGroup);

		return elementGroup;
	}
}

export class ShowCaseElementDrawer extends ElementDrawer {

	public draw(active: boolean): Konva.Group {
		const point: Point = this.convertPointToCanvas(this._sideState.getPoint(this._element, this._equipment));
		const elementGroup: Konva.Group = new Konva.Group({
			name: this._element.parent.type,
			x: point.x + 0.5,
			y: point.y + 0.5,
			width: this._element.Width * this._scale - 1,
			height: this._element.Height * this._scale - 1
		});

		elementGroup.add(new Konva.Rect({
			name: 'stroke-dark stroke-active',
			x: 0,
			y: 0,
			width: elementGroup.width(),
			height: elementGroup.height(),
			stroke: active ? konvaSettings.activeColor : this.resolveStroke(),
			strokeWidth: 1,
			fill: 'transparent'
		}));

		return elementGroup;
	}
}

export class ShelfElementDrawer extends ElementDrawer {

	public draw(active: boolean): Konva.Group {
		const point: Point = this.convertPointToCanvas(this._sideState.getPoint(this._element, this._equipment));
		const elementGroup: Konva.Group = new Konva.Group({
			name: this._element.parent.type,
			x: point.x + 0.5,
			y: point.y + 0.5,
			width: this._element.Width * this._scale - 1,
			height: this._element.Height * this._scale - 1
		});
		let elementRect: Konva.Rect, labelText: string, labelFill: string;

		if (!this._element.isExtensionActive(ExtensionType.Availability)) {
			elementRect = new Konva.Rect({
				name: 'disabled stroke-dark stroke-active fill-hover-light',
				x: 0,
				y: 0,
				width: elementGroup.width(),
				height: elementGroup.height(),
				stroke: active ? konvaSettings.activeColor : this.resolveStroke(),
				strokeWidth: 1,
				fill: konvaSettings.fillGreyColor
			});
			labelText = NOT_AVAILABLE_LABEL;
			labelFill = konvaSettings.fillGreyColor;
			elementGroup.add(elementRect);
		} else {
			elementRect = new Konva.Rect({
				name: 'dark active fill-hover-light',
				x: 0,
				y: 0,
				width: elementGroup.width(),
				height: elementGroup.height(),
				stroke: active ? konvaSettings.activeColor : this.resolveStroke(),
				strokeWidth: 1,
				fill: active ? konvaSettings.activeColor : this.resolveFill(konvaSettings.fillGreyColor)
			});
			labelText = String(Utils.getElementIndex(this._element, this._equipment));
			labelFill = konvaSettings.fillWhiteColor;
			elementGroup.add(elementRect);
		}

		this.applyFrontRounding(elementRect, elementGroup);

		if (!this._element.isExtensionActive(ExtensionType.Availability)) {
			this.drawHiddenLines(elementGroup, undefined, elementRect);
		}

		this.drawBranding(elementGroup);
		this.drawBarrier(elementGroup, active);

		const labelGroup: Konva.Group = this.createLabel(labelText, labelFill);
		labelGroup.setAttrs({ x: -labelGroup.width(), y: 0 });
		elementGroup.add(labelGroup);

		return elementGroup;
	}

	protected getTooltipValues(): Partial<ITooltipLabels> {
		return getShelfTooltipValues(this._element as ShelfElement, this._equipment);
	}
}

export class AngleShelfElementDrawer extends ElementDrawer {

	public draw(active: boolean): Konva.Group {
		const point: Point = this.convertPointToCanvas(this._sideState.getPoint(this._element, this._equipment));
		const elementGroup: Konva.Group = new Konva.Group({
			name: this._element.parent.type,
			x: point.x + 0.5,
			y: point.y + 0.5,
			width: this._element.Width * this._scale - 1,
			height: this._element.Height * this._scale - 1
		});

		let labelText: string, labelFill: string;

		if (!this._element.isExtensionActive(ExtensionType.Availability)) {
			const angleGroup: Konva.Group = new Konva.Group({
				x: 0,
				y: 0,
				width: elementGroup.width(),
				height: elementGroup.height(),
			});
			const angleRect = new Konva.Rect({
				name: 'disabled stroke-dark stroke-active fill-hover-dark',
				x: 0,
				y: 0,
				width: angleGroup.width(),
				height: angleGroup.height(),
				stroke: active ? konvaSettings.activeColor : this.resolveStroke(),
				strokeWidth: 1,
				fill: konvaSettings.fillGreyColor
			});
			angleGroup.add(angleRect);
			elementGroup.add(angleGroup);

			const frontGroup: Konva.Group = new Konva.Group({
				x: 0,
				y: elementGroup.height() - this._element.shapes[0].height * this._scale,
				width: elementGroup.width(),
				height: this._element.shapes[0].height * this._scale,
			});
			const frontRect = new Konva.Rect({
				name: 'disabled stroke-dark stroke-active fill-hover-light',
				x: 0,
				y: 0,
				width: frontGroup.width(),
				height: frontGroup.height(),
				stroke: active ? konvaSettings.activeColor : this.resolveStroke(),
				strokeWidth: 1,
				fill: konvaSettings.fillGreyColor
			});
			frontGroup.add(frontRect);
			elementGroup.add(frontGroup);

			labelText = NOT_AVAILABLE_LABEL;
			labelFill = konvaSettings.fillGreyColor;
			this.applyFrontRounding(angleRect, angleGroup);
			this.applyFrontRounding(frontRect, frontGroup);
			this.drawHiddenLines(angleGroup, undefined, angleRect);
			this.drawHiddenLines(frontGroup, undefined, frontRect);
		} else {
			elementGroup.add(new Konva.Rect({
				name: 'stroke-dark stroke-active',
				x: 0,
				y: 0,
				width: elementGroup.width(),
				height: elementGroup.height(),
				stroke: active ? konvaSettings.activeColor : this.resolveStroke(),
				strokeWidth: 1,
				fill: this.resolveFill(konvaSettings.fillGreyColor)
			}));
			elementGroup.add(new Konva.Rect({
				name: 'fill-dark fill-active fill-hover-light stroke-dark stroke-active',
				x: 0,
				y: elementGroup.height() - this._element.shapes[0].height * this._scale,
				width: elementGroup.width(),
				height: this._element.shapes[0].height * this._scale,
				stroke: active ? konvaSettings.activeColor : this.resolveStroke(),
				strokeWidth: 1,
				fill: active ? konvaSettings.activeColor : this.resolveFill(konvaSettings.fillGreyColor)
			}));
			labelText = String(Utils.getElementIndex(this._element, this._equipment));
			labelFill = konvaSettings.fillWhiteColor;
		}

		const frontHeight = this._element.shapes[0].height * this._scale;
		const brandingGroup = new Konva.Group({
			x: 0,
			y: elementGroup.height() - frontHeight,
			width: elementGroup.width(),
			height: frontHeight
		});

		elementGroup.add(brandingGroup);
		this.drawBranding(brandingGroup);
		this.drawBarrier(elementGroup, active, {
			x: 0,
			y: Utils.round(this._element.Height - this._element.shapes[0].height)
		});

		const labelGroup: Konva.Group = this.createLabel(labelText, labelFill);
		labelGroup.setAttrs({
			x: -labelGroup.width(),
			y: elementGroup.height() - this._element.shapes[0].height * this._scale
		});
		elementGroup.add(labelGroup);

		return elementGroup;
	}

	protected getTooltipValues(): Partial<ITooltipLabels> {
		return getShelfTooltipValues(this._element as ShelfElement, this._equipment);
	}
}

class HangBarFrontState implements IEquipmentSideState {

	getPoint(element: Element, equipment: Equipment): Point {
		return {
			x: element.getBoundingX().min,
			y: equipment.Height - element.getBoundingY().max
		}
	}
}

class HangBarBackState implements IEquipmentSideState {

	getPoint(element: Element, equipment: Equipment): Point {
		return {
			x: equipment.Width - element.getBoundingX().max,
			y: equipment.Height - element.getBoundingY().max
		}
	}
}

export class HangBarElementDrawer extends ElementDrawer {

	public setState(flip: boolean = false): IElementDrawer {
		this._sideState = flip ? new HangBarBackState() : new HangBarFrontState();

		return this;
	}

	public draw(active: boolean): Konva.Group {
		const point: Point = this.convertPointToCanvas(this._sideState.getPoint(this._element, this._equipment));
		const elementGroup: Konva.Group = new Konva.Group({
			name: this._element.parent.type,
			x: point.x + 0.5,
			y: point.y + 0.5,
			width: this._element.Width * this._scale - 1,
			height: this._element.Height * this._scale - 1
		});

		const elementRect = new Konva.Rect({
			name: 'fill-dark stroke-dark stroke-active fill-hover-dark',
			x: 0,
			y: 0,
			width: elementGroup.width(),
			height: elementGroup.height(),
			stroke: active ? konvaSettings.activeColor : this.resolveStroke(),
			strokeWidth: 1,
			fill: this.resolveFill(konvaSettings.fillGreyColor),
			cornerRadius: Math.max(elementGroup.height() / 2, 0)
		});

		elementGroup.add(elementRect);

		let labelText: string, labelFill: string;

		if (!this._element.isExtensionActive(ExtensionType.Availability)) {
			labelText = NOT_AVAILABLE_LABEL;
			labelFill = konvaSettings.fillGreyColor;
			this.drawHiddenLines(elementGroup, undefined, elementRect);
		} else {
			labelText = String(Utils.getElementIndex(this._element, this._equipment));
			labelFill = konvaSettings.fillWhiteColor;
		}

		const labelGroup: Konva.Group = this.createLabel(labelText, labelFill);
		labelGroup.setAttrs({
			x: -labelGroup.width(),
			y: elementGroup.height() / 2
		});
		elementGroup.add(labelGroup);

		return elementGroup;
	}

	protected getTooltipValues(): Partial<ITooltipLabels> {
		const layoutMeters: number = this._element.layoutMeters,
			freeSpace: number = Utils.round(this._element.Width - layoutMeters),
			productsGroupLength = this._element.products.map(p => p.length),
			quantitySKU = productsGroupLength.length ? productsGroupLength.reduce((a, b) => Number(a) + Number(b)) : 0;

		return {
			HANG_BAR: Utils.getElementIndex(this._element, this._equipment) || NOT_AVAILABLE_LABEL,
			LENGTH: UnitConverter.getConvertValue(this._element.Width, 'distance', 'inches', 3),
			LINEAR_METERS: UnitConverter.getConvertValue(layoutMeters, 'distance', 'feet', 3),
			FREE_SPACE_ON_THE_HANG_BAR_M: UnitConverter.getConvertValue(freeSpace, 'distance', 'inches', 3),
			QUANTITY_OF_LAID_OUT_SKU: quantitySKU
		};
	}
}

class BoxFrontState implements IEquipmentSideState {

	getPoint(element: Element, equipment: Equipment): Point {
		let end: Point = {
			x: Utils.round(element.shapes[0].begin.x + element.shapes[0].width),
			y: Utils.round(element.shapes[0].begin.y + element.shapes[0].height),
			z: element.shapes[0].begin.z
		};

		if (element.depend.parent.is(EquipmentElements.AngleShelf)) {
			end = (element.shapes[0] as Rectangle).rotatePointByFrontAngle(end);
		}

		return {
			x: element.shapes[0].begin.x,
			y: equipment.Height - end.y
		}
	}
}

class BoxBackState implements IEquipmentSideState {

	getPoint(element: Element, equipment: Equipment): Point {
		let end: Point = {
			x: Utils.round(element.shapes[0].begin.x + element.shapes[0].width),
			y: Utils.round(element.shapes[0].begin.y + element.shapes[0].height),
			z: element.shapes[0].begin.z
		};

		if (element.depend.parent.is(EquipmentElements.AngleShelf)) {
			end = (element.shapes[0] as Rectangle).rotatePointByFrontAngle(end);
		}

		return {
			x: equipment.Width - end.x,
			y: equipment.Height - end.y
		}
	}
}

export class BoxElementDrawer extends ElementDrawer {

	public setState(flip: boolean = false): IElementDrawer {
		this._sideState = flip ? new BoxBackState() : new BoxFrontState();

		return this;
	}

	public draw(active: boolean): Konva.Group {
		const point: Point = this.convertPointToCanvas(this._sideState.getPoint(this._element, this._equipment));
		let begin: Point = {
				...this._element.shapes[0].begin,
				z: Utils.round(this._element.shapes[0].begin.z + this._element.shapes[0].depth)
			},
			end: Point = {
				x: Utils.round(this._element.shapes[0].begin.x + this._element.shapes[0].width),
				y: Utils.round(this._element.shapes[0].begin.y + this._element.shapes[0].height),
				z: this._element.shapes[0].begin.z
			};

		if (this._element.depend.parent.is(EquipmentElements.AngleShelf)) {
			begin = (this._element.shapes[0] as Rectangle).rotatePointByFrontAngle(begin);
			end = (this._element.shapes[0] as Rectangle).rotatePointByFrontAngle(end);
		}

		const elementGroup: Konva.Group = new Konva.Group({
			name: this._element.parent.type,
			x: point.x + 0.5,
			y: point.y + 0.5,
			width: (end.x - begin.x) * this._scale - 1,
			height: (end.y - begin.y) * this._scale - 1
		});
		let labelText: string, labelFill: string;

		if (!this._element.isExtensionActive(ExtensionType.Availability)) {
			labelText = NOT_AVAILABLE_LABEL;
			labelFill = konvaSettings.fillGreyColor;
		} else {
			labelText = String(Utils.getElementIndex(this._element, this._equipment));
			labelFill = konvaSettings.fillWhiteColor;
		}

		const labelGroup: Konva.Group = this.createLabel(labelText, labelFill);
		const shape = this._element.shapes[0];
		const isMesh = this._element.isExtensionActive(ExtensionType.Mesh);

		if (isMesh) {
			const color = active ? konvaSettings.activeColor : this.resolveFill(konvaSettings.fillBoxColor);
			const wallWidthPx = shape.wallWidth * this._scale;

			appendBoxMeshOpenFrame(
				elementGroup,
				elementGroup.width(),
				elementGroup.height(),
				wallWidthPx,
				color
			);
			appendBoxInnerMeshGrid(
				elementGroup,
				shape.width,
				shape.height,
				shape.wallWidth,
				this._scale,
				color,
				undefined,
				elementGroup.width() / this._scale,
				elementGroup.height() / this._scale
			);
		} else {
			elementGroup.add(new Konva.Rect({
				name: 'fill-dark fill-active',
				x: 0,
				y: 0,
				width: shape.wallWidth * this._scale,
				height: elementGroup.height(),
				strokeWidth: 0,
				fill: active ? konvaSettings.activeColor : this.resolveFill(konvaSettings.fillBoxColor)
			}));

			elementGroup.add(new Konva.Rect({
				name: 'fill-dark fill-active',
				x: elementGroup.width() - shape.wallWidth * this._scale,
				y: 0,
				width: shape.wallWidth * this._scale,
				height: elementGroup.height(),
				strokeWidth: 0,
				fill: active ? konvaSettings.activeColor : this.resolveFill(konvaSettings.fillBoxColor)
			}));

			elementGroup.add(new Konva.Rect({
				name: 'front-wall stroke-dark stroke-active',
				x: 0,
				y: 0,
				width: elementGroup.width(),
				height: elementGroup.height(),
				stroke: active ? konvaSettings.activeColor : this.resolveStroke(),
				strokeWidth: 1,
				fill: this.resolveFill(konvaSettings.fillWhiteColor)
			}));
		}

		labelGroup.setAttrs({
			x: elementGroup.width() - labelGroup.width() - 1,
			y: elementGroup.height() - labelGroup.height() - 1
		});

		elementGroup.add(labelGroup);

		return elementGroup;
	}

	protected getTooltipValues(): Partial<ITooltipLabels> {
		return getBoxTooltipValues(this._element, this._equipment);
	}
}

class SlicedBoxFrontState implements IEquipmentSideState {

	getPoint(element: Element, equipment: Equipment): Point {
		let frontPoint: Point = {
				x: element.shapes[0].begin.x,
				y: Utils.round(element.shapes[0].begin.y + element.shapes[0].frontHeight),
				z: Utils.round(element.shapes[0].begin.z + element.shapes[0].depth)
			},
			backPoint: Point = {
				x: element.shapes[0].begin.x,
				y: Utils.round(element.shapes[0].begin.y + element.shapes[0].backHeight),
				z: element.shapes[0].begin.z
			};

		if (element.depend.parent.is(EquipmentElements.AngleShelf)) {
			frontPoint = (element.shapes[0] as RectangleSliced).rotatePointByFrontAngle(frontPoint);
			backPoint = (element.shapes[0] as RectangleSliced).rotatePointByFrontAngle(backPoint);
		}

		return {
			x: element.shapes[0].begin.x,
			y: equipment.Height - Math.max(frontPoint.y, backPoint.y)
		}
	}
}

class SlicedBoxBackState implements IEquipmentSideState {

	getPoint(element: Element, equipment: Equipment): Point {
		let frontPoint: Point = {
				x: Utils.round(element.shapes[0].begin.x + element.shapes[0].width),
				y: Utils.round(element.shapes[0].begin.y + element.shapes[0].frontHeight),
				z: Utils.round(element.shapes[0].begin.z + element.shapes[0].depth)
			},
			backPoint: Point = {
				x: Utils.round(element.shapes[0].begin.x + element.shapes[0].width),
				y: Utils.round(element.shapes[0].begin.y + element.shapes[0].backHeight),
				z: element.shapes[0].begin.z
			};

		if (element.depend.parent.is(EquipmentElements.AngleShelf)) {
			frontPoint = (element.shapes[0] as RectangleSliced).rotatePointByFrontAngle(frontPoint);
			backPoint = (element.shapes[0] as RectangleSliced).rotatePointByFrontAngle(backPoint);
		}

		return {
			x: equipment. Width - frontPoint.x,
			y: equipment.Height - Math.max(frontPoint.y, backPoint.y)
		}
	}
}

export class SlicedBoxElementDrawer extends ElementDrawer {

	public setState(flip: boolean = false): IElementDrawer {
		this._sideState = flip ? new SlicedBoxBackState() : new SlicedBoxFrontState();

		return this;
	}

	public draw(active: boolean): Konva.Group {
		const point: Point = this.convertPointToCanvas(this._sideState.getPoint(this._element, this._equipment));
		let begin: Point = {
				...this._element.shapes[0].begin,
				z: Utils.round(this._element.shapes[0].begin.z + this._element.shapes[0].depth)
			},
			topBegin: Point = {
				x: this._element.shapes[0].begin.x,
				y: Utils.round(this._element.shapes[0].begin.y + this._element.shapes[0].frontHeight),
				z: Utils.round(this._element.shapes[0].begin.z + this._element.shapes[0].depth)
			},
			end: Point = {
				x: Utils.round(this._element.shapes[0].begin.x + this._element.shapes[0].width),
				y: Utils.round(this._element.shapes[0].begin.y + this._element.shapes[0].backHeight),
				z: this._element.shapes[0].begin.z
			};

		if (this._element.depend.parent.is(EquipmentElements.AngleShelf)) {
			begin = (this._element.shapes[0] as RectangleSliced).rotatePointByFrontAngle(begin);
			topBegin = (this._element.shapes[0] as RectangleSliced).rotatePointByFrontAngle(topBegin);
			end = (this._element.shapes[0] as RectangleSliced).rotatePointByFrontAngle(end);
		}

		const elementGroup: Konva.Group = new Konva.Group({
			name: this._element.parent.type,
			x: point.x,
			y: point.y,
			width: (end.x - begin.x) * this._scale,
			height: (Math.max(topBegin.y, end.y) - begin.y) * this._scale
		});
		let labelText: string, labelFill: string;

		if (!this._element.isExtensionActive(ExtensionType.Availability)) {
			labelText = NOT_AVAILABLE_LABEL;
			labelFill = konvaSettings.fillGreyColor;
		} else {
			labelText = String(Utils.getElementIndex(this._element, this._equipment));
			labelFill = konvaSettings.fillWhiteColor;
		}

		const labelGroup: Konva.Group = this.createLabel(labelText, labelFill);
		const isMesh = this._element.isExtensionActive(ExtensionType.Mesh);
		const shape = this._element.shapes[0] as RectangleSliced;
		const bboxHeight = Math.max(topBegin.y, end.y) - begin.y;

		if (isMesh) {
			const color = active ? konvaSettings.activeColor : this.resolveFill(konvaSettings.fillBoxColor);
			const wallWidthPx = shape.wallWidth * this._scale;

			appendBoxMeshOpenFrame(
				elementGroup,
				elementGroup.width(),
				elementGroup.height(),
				wallWidthPx,
				color
			);
			appendSlicedBoxInnerMeshGrid(
				elementGroup,
				shape.width,
				shape.frontHeight,
				shape.backHeight,
				shape.wallWidth,
				this._scale,
				color,
				1,
				SLICED_BOX_FRONT_INNER_LINE_WIDTH_PX,
				elementGroup.width() / this._scale,
				elementGroup.height() / this._scale
			);
		} else {
			elementGroup.add(new Konva.Rect({
				name: 'back-wall stroke-darker stroke-active',
				x: 0.5,
				y: 0.5,
				width: elementGroup.width() - 1,
				height: elementGroup.height() - 1,
				stroke: active ? konvaSettings.activeColor : darkenColor(this.resolveStroke()),
				strokeWidth: 1,
				fill: konvaSettings.fillGreyColor,
			}));
			elementGroup.add(new Konva.Rect({
				name: 'stroke-dark stroke-active',
				x: 0,
				y: 1,
				width: shape.wallWidth * this._scale,
				height: elementGroup.height() - 1,
				stroke: active ? konvaSettings.activeColor : this.resolveStroke(),
				strokeWidth: 1,
				fill: this.resolveFill(konvaSettings.fillWhiteColor)
			}));
			elementGroup.add(new Konva.Rect({
				name: 'stroke-dark stroke-active',
				x: elementGroup.width() - shape.wallWidth * this._scale,
				y: 1,
				width: shape.wallWidth * this._scale,
				height: elementGroup.height() - 1,
				stroke: active ? konvaSettings.activeColor : this.resolveStroke(),
				strokeWidth: 1,
				fill: this.resolveFill(konvaSettings.fillWhiteColor)
			}));
			elementGroup.add(new Konva.Rect({
				name: 'front-wall stroke-dark stroke-active',
				x: 0.5,
				y: 0.5 + (Math.max(topBegin.y, end.y) - topBegin.y) * this._scale,
				width: elementGroup.width() - 1,
				height: (topBegin.y - begin.y) * this._scale - 1,
				stroke: active ? konvaSettings.activeColor : this.resolveStroke(),
				strokeWidth: 1,
				fill: this.resolveFill(konvaSettings.fillWhiteColor)
			}));
		}

		labelGroup.setAttrs({
			x: elementGroup.width() - labelGroup.width() - 1,
			y: elementGroup.height() - labelGroup.height() - 1
		});

		elementGroup.add(labelGroup);

		return elementGroup;
	}

	protected getTooltipValues(): Partial<ITooltipLabels> {
		return getBoxTooltipValues(this._element, this._equipment);
	}
}

export class HookElementDrawer extends ElementDrawer {

	public draw(active: boolean): Konva.Group {
		const point: Point = this.convertPointToCanvas(this._sideState.getPoint(this._element, this._equipment));
		const elementGroup: Konva.Group = new Konva.Group({
			name: this._element.parent.type,
			x: point.x + 0.5,
			y: point.y + 0.5,
			width: this._element.Width * this._scale - 1,
			height: this._element.Height * this._scale - 1
		});

		/*elementGroup.add(new Konva.Rect({
			name: 'clickable-offset',
			x: -elementGroup.width(),
			y: -elementGroup.height(),
			width: elementGroup.width() * 3,
			height: elementGroup.height() * 3,
			strokeWidth: 0,
			fill: 'transparent'
		}));*/

		if (!this._element.isExtensionActive(ExtensionType.Availability)) {
			const elementRect = new Konva.Rect({
				name: 'disabled stroke-dark stroke-active fill-hover-light',
				x: 0,
				y: 0,
				width: elementGroup.width(),
				height: elementGroup.height(),
				stroke: active ? konvaSettings.activeColor : this.resolveStroke(),
				strokeWidth: 1,
				fill: konvaSettings.fillGreyColor
			});

			elementGroup.add(elementRect);
			this.applyFrontRounding(elementRect, elementGroup);
			this.drawHiddenLines(elementGroup, undefined, elementRect);
		} else {
			const elementRect = new Konva.Rect({
				name: 'dark active fill-hover-light',
				x: 0,
				y: 0,
				width: elementGroup.width(),
				height: elementGroup.height(),
				stroke: active ? konvaSettings.activeColor : this.resolveStroke(),
				strokeWidth: 1,
				fill: active ? konvaSettings.activeColor : this.resolveFill(konvaSettings.fillGreyColor)
			});

			elementGroup.add(elementRect);
			this.applyFrontRounding(elementRect, elementGroup);
		}

		return elementGroup;
	}

	protected getTooltipValues(): Partial<ITooltipLabels> {
		const layoutMeters: number = this._element.layoutMeters,
			freeSpaceOnHook = this._element.Depth - layoutMeters,
			productsGroupLength = this._element.products.map(p => p.length),
			quantitySKU = productsGroupLength.length ? productsGroupLength.reduce((a, b) => Number(a) + Number(b)) : 0;

		return {
			ONE_HOOK: Utils.getElementIndex(this._element, this._equipment) || NOT_AVAILABLE_LABEL,
			DEPTH: UnitConverter.getConvertValue(this._element.Depth, 'distance', 'inches', 3),
			LINEAR_METERS: UnitConverter.getConvertValue(layoutMeters, 'distance', 'feet', 3),
			FREE_SPACE_ON_THE_HOOK_M: UnitConverter.getConvertValue(freeSpaceOnHook, 'distance', 'inches', 3),
			QUANTITY_OF_LAID_OUT_SKU: quantitySKU
		};
	}
}

class PartitionFrontState implements IEquipmentSideState {

	getPoint(element: Element, equipment: Equipment): Point {
		let end: Point = {
			x: Utils.round(element.shapes[0].begin.x + element.shapes[0].width),
			y: Utils.round(element.shapes[0].begin.y + element.shapes[0].height),
			z: element.shapes[0].begin.z
		};

		if (element.depend.parent.is(EquipmentElements.AngleShelf)) {
			end = (element.shapes[0] as Rectangle).rotatePointByFrontAngle(end);
		}

		return {
			x: element.shapes[0].begin.x,
			y: equipment.Height - end.y
		}
	}
}

class PartitionBackState implements IEquipmentSideState {

	getPoint(element: Element, equipment: Equipment): Point {
		let end: Point = {
			x: Utils.round(element.shapes[0].begin.x + element.shapes[0].width),
			y: Utils.round(element.shapes[0].begin.y + element.shapes[0].height),
			z: element.shapes[0].begin.z
		};

		if (element.depend.parent.is(EquipmentElements.AngleShelf)) {
			end = (element.shapes[0] as Rectangle).rotatePointByFrontAngle(end);
		}

		return {
			x: equipment.Width - end.x,
			y: equipment.Height - end.y
		}
	}
}

export class PartitionElementDrawer extends ElementDrawer {

	public setState(flip: boolean = false): IElementDrawer {
		this._sideState = flip ? new PartitionBackState() : new PartitionFrontState();

		return this;
	}

	public draw(active: boolean): Konva.Group {
		const point: Point = this.convertPointToCanvas(this._sideState.getPoint(this._element, this._equipment));
		let begin: Point = {
				...this._element.shapes[0].begin,
				z: Utils.round(this._element.shapes[0].begin.z + this._element.shapes[0].depth)
			},
			end: Point = {
				x: Utils.round(this._element.shapes[0].begin.x + this._element.shapes[0].width),
				y: Utils.round(this._element.shapes[0].begin.y + this._element.shapes[0].height),
				z: this._element.shapes[0].begin.z
			};

		if (this._element.depend.parent.is(EquipmentElements.AngleShelf)) {
			begin = (this._element.shapes[0] as Rectangle).rotatePointByFrontAngle(begin);
			end = (this._element.shapes[0] as Rectangle).rotatePointByFrontAngle(end);
		}

		const elementGroup: Konva.Group = new Konva.Group({
			name: this._element.parent.type,
			x: point.x + 0.5,
			y: point.y + 0.5,
			width: (end.x - begin.x) * this._scale - 1,
			height: (end.y - begin.y) * this._scale - 1
		});

		elementGroup.add(new Konva.Rect({
			name: 'dark active',
			x: 0,
			y: 0,
			width: elementGroup.width(),
			height: elementGroup.height(),
			stroke: active ? konvaSettings.activeColor : this.resolveStroke(),
			strokeWidth: 1,
			fill: active ? konvaSettings.activeColor : this.resolveFill(konvaSettings.fillGreyColor)
		}));

		return elementGroup;
	}
}

export class ElementDrawerFactory {

	private static drawers: { [key in EquipmentElements]: typeof ElementDrawer } = {
		[EquipmentElements.BaseRectangle]: RectangleBaseElementDrawer,
		[EquipmentElements.BaseTrapeze]: TrapezeBaseElementDrawer,
		[EquipmentElements.BackWall]: BackWallElementDrawer,
		[EquipmentElements.CentralPillar]: CentralPillarElementDrawer,
		[EquipmentElements.HookWall]: HookWallElementDrawer,
		[EquipmentElements.PerforatedWall]: PerforatedWallElementDrawer,
		[EquipmentElements.SideWalls]: SideWallsElementDrawer,
		[EquipmentElements.Showcase]: ShowCaseElementDrawer,
		[EquipmentElements.Shelf]: ShelfElementDrawer,
		[EquipmentElements.AngleShelf]: AngleShelfElementDrawer,
		[EquipmentElements.HangBar]: HangBarElementDrawer,
		[EquipmentElements.Box]: BoxElementDrawer,
		[EquipmentElements.SlicedBox]: SlicedBoxElementDrawer,
		[EquipmentElements.HookPanel]: HookWallElementDrawer,
		[EquipmentElements.Hook]: HookElementDrawer,
		[EquipmentElements.Perforation]: PerforatedWallElementDrawer,
		[EquipmentElements.Hole]: ElementDrawer,
		[EquipmentElements.Partition]: PartitionElementDrawer
	}

	public static getDrawer(element: Element): ElementDrawer {
		if (ElementDrawerFactory.drawers.hasOwnProperty(element.parent.type)) {
			return new ElementDrawerFactory.drawers[element.parent.type](element);
		} else {
			throw new Error(`No such element drawer ${element.parent.type}`);
		}
	}
}
